/**
 * ABAC Constants
 *
 * Defines resource types and actions that match the backend ABAC system.
 * Keep in sync with FlexApp/Attributes/AuthorizeAbacAttribute.cs
 */

/**
 * Resource types for ABAC authorization
 * Matches backend AbacResourceTypes
 */
export const RESOURCE_TYPES = {
  ASSET: "Asset",
  LOCATION: "Location",
  WORK_ORDER: "WorkOrder",
  TICKET: "Ticket",
  PART: "Part",
  PRODUCT: "Product",
  TEAM: "Team",
  DEPARTMENT: "Department",
  ROLE: "Role",
  USER: "User",
  ORGANIZATION: "Organization",
  POLICY: "Policy",
  REPORT: "Report",
  INVOICE: "Invoice",
  ORDER: "Order",
  ORGANIZATION_UNIT: "OrganizationUnit",
  AUDIT_LOG: "AuditLog",
  TENANT_MANAGEMENT: "TenantManagement",
  // Added with the backend catalog expansion (44 total). Keep in sync with the
  // AbacResourceTypes constants + AbacCatalog* seed migrations on flex2-api.
  DEVICE: "Device",
  INSPECTION: "Inspection",
  JOB: "Job",
  NOTE: "Note",
  NOTIFICATION: "Notification",
  DOCUMENT: "Document",
  FINANCIAL: "Financial",
  SUBSCRIPTION: "Subscription",
  ENTITLEMENT: "Entitlement",
  SPEND_GUARD: "SpendGuard",
  QUOTATION: "Quotation",
  // Standalone labor catalog (e.g. "Lift Inspection — $175/visit").
  // Distinct from the Manufacturer/Model/Product bundle behind /servive-parts.
  SERVICE: "Service",
  ASSET_CLASS: "AssetClass",
  SKU: "Sku",
  PRODUCT_CATEGORY: "ProductCategory",
  TAXONOMY: "Taxonomy",
  RECYCLING_TYPE: "RecyclingType",
  LEAD: "Lead",
  AGENT: "Agent",
  LISTING: "Listing",
  VENDOR: "Vendor",
  ACTIVITY: "Activity",
  APPROVAL: "Approval",
  CRON_JOB: "CronJob",
  EXPORT_RESOURCE: "Export",
  WIDGET: "Widget",
  QUALIFICATION: "Qualification",
};

/**
 * Action types for ABAC authorization
 * Matches backend AbacActions
 */
export const ACTIONS = {
  VIEW: "View",
  CREATE: "Create",
  EDIT: "Edit",
  DELETE: "Delete",
  EXPORT: "Export",
  IMPORT: "Import",
  APPROVE: "Approve",
  PUBLISH: "Publish",
  ASSIGN: "Assign",
  ARCHIVE: "Archive",
  RESTORE: "Restore",
  MANAGE: "Manage",
};

/**
 * Cache configuration
 */
export const CACHE_CONFIG = {
  MAX_SIZE: 1000,
  TTL_MS: 5 * 60 * 1000, // 5 minutes
};

/**
 * Batch configuration
 */
export const BATCH_CONFIG = {
  DELAY_MS: 50,
  MAX_SIZE: 50,
};

/**
 * Permission evaluation results
 */
export const PERMISSION_RESULT = {
  GRANTED: "granted",
  DENIED: "denied",
  ERROR: "error",
  PENDING: "pending",
};

/**
 * Helper to check if a resource type is valid
 */
export const isValidResourceType = (resourceType) => {
  return Object.values(RESOURCE_TYPES).includes(resourceType);
};

/**
 * Helper to check if an action is valid
 */
export const isValidAction = (action) => {
  return Object.values(ACTIONS).includes(action);
};

/**
 * Generate a permission key for caching
 */
export const generatePermissionKey = (resourceType, action, resourceId = null) => {
  if (resourceId) {
    return `${resourceType}:${action}:${resourceId}`;
  }
  return `${resourceType}:${action}`;
};

export default {
  RESOURCE_TYPES,
  ACTIONS,
  CACHE_CONFIG,
  BATCH_CONFIG,
  PERMISSION_RESULT,
  isValidResourceType,
  isValidAction,
  generatePermissionKey,
};
